import { get_guid } from '../visual-analytics/helpers/misc';
import * as dagre from 'dagre';

export interface TpAssetNetworkNode {
  id: string;
  assetId: string;
  name: string;
  typeName: string;
  width: number;
  height: number;
  xPos: number;
  yPos: number;
  randomVal: number;
  temp: number;

  rank: number;
  links: TpAssetNetworkNode[];
  linksUpstream: TpAssetNetworkNode[];
  linksDownstream: TpAssetNetworkNode[];
}

export interface TpAssetNetworkRelation {
  node1: TpAssetNetworkNode;
  node2: TpAssetNetworkNode;
  //controlPoints: { x: number, y: number }[], // Currently not used
}

export interface TpAssetNetwork {
  nodeWidth: number;
  nodeHeight: number;
  horSep: number; // horizontal separation between nodes
  vertSep: number; // vertical separation nodes
  totWidth: number;
  totHeight: number;
  assets: TpAssetNetworkNode[];
  relations: TpAssetNetworkRelation[];
  ranks: TpAssetNetworkNode[][];
}

export function createAssetNetwork(
  nodeWidth: number,
  nodeHeight: number,
  horSep: number,
  vertSep: number,
): TpAssetNetwork {
  return {
    nodeWidth,
    nodeHeight,
    horSep,
    vertSep,
    totWidth: 0,
    totHeight: 0,
    assets: [],
    relations: [],
    ranks: [],
  };
}

export function createAssetNode(
  network: TpAssetNetwork,
  name: string,
  typeName: string,
  assetId: string,
  parentNodes: TpAssetNetworkNode[],
): TpAssetNetworkNode {
  const node: TpAssetNetworkNode = {
    id: get_guid(),
    assetId,
    name,
    typeName,
    width: network.nodeWidth,
    height: network.nodeHeight,
    xPos: 0,
    yPos: 0,
    links: [],
    linksUpstream: [],
    linksDownstream: [],
    rank: -1,
    randomVal: 0,
    temp: 0,
  };
  network.assets.push(node);
  for (const pNode of parentNodes) {
    network.relations.push({ node1: pNode, node2: node });
    node.links.push(pNode);
    pNode.links.push(node);
    pNode.linksDownstream.push(node);
    node.linksUpstream.push(pNode);
  }
  return node;
}

function optimizeYposits(network: TpAssetNetwork, vertSep: number) {
  for (const rank of network.ranks) rank.sort((a, b) => a.yPos - b.yPos);
  const ranksReversed = network.ranks.slice().reverse();

  const iterCount = 10;
  for (let iter = 0; iter < iterCount; iter++) {
    //reposition entire ranks towards the barycentric optimal position
    for (const rank of network.ranks) {
      let totalBaryCentricShift = 0;
      for (const node of rank)
        if (node.links.length > 0)
          totalBaryCentricShift +=
            node.links.reduce((accumulator, link) => accumulator + link.yPos, 0) / node.links.length - node.yPos;
      for (const node of rank) node.yPos += totalBaryCentricShift / rank.length;
    }

    const fr1 = Math.min(1, 0.5 + iter / iterCount); // we evolve from a compactness optimization to a barycentric optimization
    const fr2 = 1 - fr1;
    const overlap = 0;

    for (let dirRanks = 0; dirRanks <= 1; dirRanks++) {
      for (const rank of dirRanks == 0 ? network.ranks : ranksReversed) {
        for (let idxIter = 0; idxIter < rank.length; idxIter++) {
          const idx = dirRanks == 0 ? idxIter : rank.length - 1 - idxIter;
          const node = rank[idx];

          const links = dirRanks == 0 ? node.linksUpstream : node.linksDownstream; // node.links;

          let avg = node.yPos;
          if (links.length > 0) avg = links.reduce((accumulator, link) => accumulator + link.yPos, 0) / links.length;

          if (idx > 0) {
            const minVal = rank[idx - 1].yPos + vertSep - overlap;
            avg = fr1 * Math.max(avg, minVal) + fr2 * minVal;
          }
          if (idx < rank.length - 1) {
            const maxVal = rank[idx + 1].yPos - vertSep + overlap;
            avg = fr1 * Math.min(avg, maxVal) + fr2 * maxVal;
          }
          node.yPos = avg;
        }
      }
    }
  }

  let minYPos = 1e99;
  for (const asset of network.assets) minYPos = Math.min(minYPos, asset.yPos);

  network.totWidth = 0;
  network.totHeight = 0;
  for (const asset of network.assets) {
    asset.yPos -= minYPos;
    network.totWidth = Math.max(network.totWidth, asset.xPos + asset.width);
    network.totHeight = Math.max(network.totHeight, asset.yPos + asset.height);
  }
}

export function layoutAssetNetworkDagre(network: TpAssetNetwork) {
  const nodesMap: { [id: string]: TpAssetNetworkNode } = {};
  for (const node of network.assets) nodesMap[node.id] = node;

  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: 'LR', //  left to right layout
    nodesep: network.vertSep, // vertical separation
    edgesep: 0,
    ranksep: network.horSep, // horizontal separation
    ranker: 'tight-tree', //'network-simplex'
  });
  g.setDefaultEdgeLabel(function () {
    return {};
  });

  for (const node of network.assets) g.setNode(node.id, { width: node.width, height: node.height });
  for (const edge of network.relations) g.setEdge(edge.node1.id, edge.node2.id);

  dagre.layout(g);

  network.ranks = [];
  for (const asset of network.assets) {
    asset.xPos = g.node(asset.id).x - asset.width / 2; // dagre returns the center of the node!
    asset.yPos = g.node(asset.id).y - asset.height / 2;
    asset.rank = Math.round(asset.xPos / (network.horSep + network.nodeWidth));
    while (network.ranks.length <= asset.rank) network.ranks.push([]);
    network.ranks[asset.rank].push(asset);
  }

  optimizeYposits(network, network.vertSep + network.nodeHeight);

  // for (const rel of network.relations) {
  //     const edgeLayout = g.edge({ v: rel.node1.id, w: rel.node2.id });
  //     rel.controlPoints = edgeLayout.points;
  // }
}

export function layoutAssetNetworkFast(network: TpAssetNetwork) {
  // STEP1: determine ranks of each node
  let ranksModified = true;
  let maxRank = 0;
  while (ranksModified) {
    ranksModified = false;
    for (const node of network.assets) {
      if (node.rank < 0) {
        let upstreamCompleted = true;
        let upstreamMaxRank = -1;
        for (const upstreamNode of node.linksUpstream) {
          if (upstreamNode.rank < 0) upstreamCompleted = false;
          if (upstreamMaxRank < upstreamNode.rank) upstreamMaxRank = upstreamNode.rank;
        }
        if (upstreamCompleted) {
          node.rank = upstreamMaxRank + 1;
          if (maxRank < node.rank) maxRank = node.rank;
          ranksModified = true;
        }
      }
    }
  }
  network.ranks = [];
  for (let i = 0; i <= maxRank; i++) network.ranks.push([]);
  for (const node of network.assets) {
    node.yPos = network.ranks[node.rank].length;
    network.ranks[node.rank].push(node);
  }

  const ranksReversed = network.ranks.slice().reverse();

  // STEP 2: optimize vertical ordering within a rank, using barycentric iteration

  for (let iter0 = 0; iter0 < 5; iter0++) {
    for (let iter = 0; iter < 20; iter++) {
      for (const rank of network.ranks)
        for (const node of rank)
          if (node.linksUpstream.length > 0)
            node.yPos =
              0.5 * node.yPos +
              (0.5 * node.linksUpstream.reduce((accumulator, link) => accumulator + link.yPos, 0)) /
                node.linksUpstream.length;

      for (const rank of ranksReversed)
        for (const node of rank)
          if (node.linksDownstream.length > 0)
            node.yPos =
              0.5 * node.yPos +
              (0.5 * node.linksDownstream.reduce((accumulator, link) => accumulator + link.yPos, 0)) /
                node.linksDownstream.length;

      for (const rank of network.ranks)
        for (const node of rank)
          node.temp =
            0.5 * node.yPos +
            (0.5 * node.links.reduce((accumulator, link) => accumulator + link.yPos, 0)) / node.links.length;
      for (const rank of network.ranks) for (const node of rank) node.yPos = node.temp;

      // eslint-disable-next-line no-constant-condition
      if (true) {
        // re-normalize heights
        let minY = 1e99;
        let maxY = -1e99;
        for (const asset of network.assets) {
          minY = Math.min(minY, asset.yPos);
          maxY = Math.max(maxY, asset.yPos);
        }
        for (const asset of network.assets) asset.yPos = ((asset.yPos - minY) / (maxY - minY)) * 30;
      }
    }

    // eslint-disable-next-line no-constant-condition
    if (true) {
      //push away overlap

      for (const rank of network.ranks) rank.sort((a, b) => a.yPos - b.yPos);

      const iterCount = 10;
      for (let iter = 0; iter <= iterCount; iter++) {
        const fr = 0.2 + (0.8 * iter) / iterCount;
        for (const rank of network.ranks) {
          let blockPos = -1e99;
          for (const node of rank) {
            if (node.yPos < blockPos) node.yPos = (1 - fr) * node.yPos + fr * blockPos;
            blockPos = node.yPos + 1;
          }
          blockPos = 1e99;
          for (let idx = rank.length - 1; idx >= 0; idx--) {
            const node = rank[idx];
            if (node.yPos > blockPos) node.yPos = (1 - fr) * node.yPos + fr * blockPos;
            blockPos = node.yPos - 1;
          }
        }
      }
    }
  }

  // STEP3: optimize Y layout
  for (const rank of network.ranks) rank.sort((a, b) => a.yPos - b.yPos);
  for (let iter = 0; iter < 10; iter++) {
    for (const rank of network.ranks) {
      let idx = 0;
      for (const node of rank) {
        if (idx > 0 && rank[idx - 1].yPos > node.yPos) debugger;
        let avg = node.links.reduce((accumulator, link) => accumulator + link.yPos, 0) / node.links.length;
        if (avg < node.yPos) {
          if (idx > 0) avg = Math.max(avg, rank[idx - 1].yPos + 1);
          node.yPos = avg;
        }
        idx++;
      }

      for (let idx = rank.length - 1; idx >= 0; idx--) {
        const node = rank[idx];
        let avg = node.links.reduce((accumulator, link) => accumulator + link.yPos, 0) / node.links.length;
        if (avg > node.yPos) {
          if (idx < rank.length - 1) avg = Math.min(avg, rank[idx + 1].yPos - 1);
          node.yPos = avg;
        }
      }
    }
  }

  // FINAL

  for (const node of network.assets) {
    node.xPos = node.rank * 300;
    node.yPos = node.yPos * 60;
  }

  network.totWidth = 0;
  let minH = 1e99;
  let maxH = -1e99;
  network.totHeight = 0;
  for (const asset of network.assets) {
    network.totWidth = Math.max(network.totWidth, asset.xPos + asset.width);
    minH = Math.min(minH, asset.yPos);
    maxH = Math.max(maxH, asset.yPos + asset.height);
  }
  for (const asset of network.assets) asset.yPos -= minH;
  network.totHeight = maxH - minH;
}
